import React, { useEffect, useRef, useState } from 'react';
import VerdictAssetImage from './VerdictAssetImage';
import { getCanonicalRenderedVerdictAsset } from '../utils/verdictAssets';

const STYLE_OPTIONS = ['bold', 'clean', 'playful'];

const hashString = (input) => {
  if (!input) return 0;
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const selectVerdictFormat = ({
  id,
  verdictLine,
  hasImage,
}) => {
  const lineLength = (verdictLine || '').length;
  if (!hasImage) return 'caption';
  if (lineLength > 0 && lineLength < 60) return 'overlay';
  if (lineLength > 0 && lineLength < 120) return 'sticker';
  return 'caption';
};

const selectStyleVariant = ({
  id,
  format,
}) => {
  const idx = hashString(`${id || ''}-${format || ''}`) % STYLE_OPTIONS.length;
  return STYLE_OPTIONS[idx] || 'clean';
};

const styleClasses = {
  bold: 'font-black uppercase tracking-tight',
  clean: 'font-semibold',
  playful: 'font-bold',
};

const textOverlayStyle = {
  textShadow: '0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.6)',
};

const isVerdictCardSvgAsset = (url) => {
  const normalized = String(url || '').toLowerCase();
  return normalized.includes('/verdict-card.svg') || normalized.includes('/premium-verdict-card.svg');
};

const isPremiumVerdictCardSvgAsset = (url) => String(url || '').toLowerCase().includes('/premium-verdict-card.svg');
const PREMIUM_COMPOSED_SURFACES = new Set([
  'detail-trial',
  'detail-trial-premium',
  'trial-detail-premium-hero',
  'trial-detail-export',
  'detail-daily',
]);

export const deriveOverlayVerdictLine = (input, maxLen = 58) => {
  const clean = String(input || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\.\.\./g, '')
    .replace(/…/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return '';
  if (clean.length <= maxLen) return clean;
  const slice = clean.slice(0, maxLen).trim();
  const lastSpace = slice.lastIndexOf(' ');
  const shortened = (lastSpace > 20 ? slice.slice(0, lastSpace) : slice).trim();
  return shortened.replace(/[.,;:!?-]+$/g, '').trim();
};

const NoImageState = ({
  heightClass,
  title,
  verdictLine,
  winnerLabel,
  loserLabel,
  sideA,
  sideB,
  isDaily,
}) => (
  <div className={`w-full ${heightClass} bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.18),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.18),_transparent_38%),linear-gradient(180deg,rgba(15,23,42,0.98),rgba(2,6,23,1))]`}>
    <div className="flex h-full flex-col justify-between px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-[76%]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-300/75">
            {isDaily ? 'Daily Verdict' : 'Trial Verdict'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-100 line-clamp-2">{title}</div>
        </div>
        <div className="rounded-full border border-slate-500/30 bg-slate-900/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">
          Verdict Card
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-2xl border border-slate-700/80 bg-slate-950/45 p-3">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Side A</div>
          <div className="mt-1 text-sm font-semibold text-slate-100 line-clamp-2">{sideA || winnerLabel || 'Option A'}</div>
        </div>
        <div className="rounded-2xl border border-slate-700/80 bg-slate-950/45 p-3">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Side B</div>
          <div className="mt-1 text-sm font-semibold text-slate-100 line-clamp-2">{sideB || loserLabel || 'Option B'}</div>
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-slate-700/80 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Verdict</div>
        <div className="mt-1 text-sm font-bold text-slate-100 line-clamp-3">
          {verdictLine || (winnerLabel ? `Winner: ${winnerLabel}` : 'Verdict available')}
        </div>
        {(winnerLabel || loserLabel) && (
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-300">
            {winnerLabel && (
              <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-1">
                Winner: {winnerLabel}
              </span>
            )}
            {loserLabel && (
              <span className="rounded-full border border-slate-600/70 bg-slate-800/60 px-2 py-1">
                Other: {loserLabel}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const FrameShell = ({ imageUrl, assetSource, assetId, title, heightClass, children, surface, verdictLine, winnerLabel, loserLabel, sideA, sideB, isDaily, isVerdictCardSvg = false }) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [imageUrl, assetId]);

  return (
    <div className="relative overflow-hidden">
      {imageUrl && !imageLoadFailed ? (
        <VerdictAssetImage
          imageUrl={imageUrl}
          assetSource={assetSource}
          screen={surface}
          trialId={assetId}
          alt={title}
          className={`w-full ${heightClass} object-cover`}
          onAllFailed={() => setImageLoadFailed(true)}
        />
      ) : (
        <NoImageState
          heightClass={heightClass}
          title={title}
          verdictLine={verdictLine}
          winnerLabel={winnerLabel}
          loserLabel={loserLabel}
          sideA={sideA}
          sideB={sideB}
          isDaily={isDaily}
        />
      )}
      {!isVerdictCardSvg && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-[25%] min-h-[68px] bg-gradient-to-t from-slate-950 via-slate-950/92 to-slate-950/20" />
        </>
      )}
      {children}
      {!isVerdictCardSvg && (
        <div className="absolute right-2 top-2 rounded-full bg-slate-950/55 px-2 py-1 text-[9px] uppercase tracking-[0.2em] text-slate-200/75">
          GweffIT Verdict
        </div>
      )}
    </div>
  );
};

const CaptionFrame = ({ imageUrl, assetSource, assetId, title, verdictLine, emoji, heightClass, showEmoji, textClass, winnerLabel, loserLabel, sideA, sideB, isDaily, surface, renderOverlayText }) => (
  <FrameShell imageUrl={imageUrl} assetSource={assetSource} assetId={assetId} title={title} emoji={emoji} heightClass={heightClass} showEmoji={showEmoji} surface={surface} verdictLine={verdictLine} winnerLabel={winnerLabel} loserLabel={loserLabel} sideA={sideA} sideB={sideB} isDaily={isDaily} isVerdictCardSvg={!renderOverlayText}>
    {renderOverlayText && (
      <div className="absolute inset-x-0 bottom-0 flex min-h-[68px] items-end px-3 pb-3 pt-6">
        <div style={textOverlayStyle} className={`w-full pr-2 text-[13px] text-slate-50 sm:text-sm line-clamp-2 ${textClass}`}>
          {verdictLine || (winnerLabel ? `Winner: ${winnerLabel}` : '')}
        </div>
      </div>
    )}
  </FrameShell>
);

const OverlayFrame = ({ imageUrl, assetSource, assetId, title, verdictLine, emoji, heightClass, showEmoji, textClass, winnerLabel, loserLabel, sideA, sideB, isDaily, surface, renderOverlayText }) => (
  <FrameShell imageUrl={imageUrl} assetSource={assetSource} assetId={assetId} title={title} emoji={emoji} heightClass={heightClass} showEmoji={showEmoji} surface={surface} verdictLine={verdictLine} winnerLabel={winnerLabel} loserLabel={loserLabel} sideA={sideA} sideB={sideB} isDaily={isDaily} isVerdictCardSvg={!renderOverlayText}>
    {renderOverlayText && (
      <div className="absolute inset-x-0 bottom-0 flex min-h-[68px] items-end px-3 pb-3 pt-6">
        <div style={textOverlayStyle} className={`w-full pr-2 text-[13px] text-slate-50 sm:text-sm line-clamp-2 ${textClass}`}>
          {verdictLine || (winnerLabel ? `Winner: ${winnerLabel}` : '')}
        </div>
      </div>
    )}
  </FrameShell>
);

const StickerFrame = ({ imageUrl, assetSource, assetId, title, verdictLine, emoji, heightClass, showEmoji, textClass, winnerLabel, loserLabel, sideA, sideB, isDaily, surface, renderOverlayText }) => (
  <FrameShell imageUrl={imageUrl} assetSource={assetSource} assetId={assetId} title={title} emoji={emoji} heightClass={heightClass} showEmoji={showEmoji} surface={surface} verdictLine={verdictLine} winnerLabel={winnerLabel} loserLabel={loserLabel} sideA={sideA} sideB={sideB} isDaily={isDaily} isVerdictCardSvg={!renderOverlayText}>
    {renderOverlayText && (
      <div className="absolute inset-x-0 bottom-0 flex min-h-[68px] items-end px-3 pb-3 pt-6">
        <div className={`w-full rounded-2xl bg-white/95 px-3 py-2 text-[12px] text-slate-900 shadow-lg sm:text-[13px] line-clamp-2 ${textClass}`}>
          {verdictLine || (winnerLabel ? `Winner: ${winnerLabel}` : '')}
        </div>
      </div>
    )}
  </FrameShell>
);

const PremiumComposedFrame = ({
  imageUrl,
  assetSource,
  assetId,
  title,
  winnerLabel,
  loserLabel,
  sideA,
  sideB,
  isDaily,
  surface,
}) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [imageUrl, assetId]);

  return (
    <div className="overflow-hidden rounded-none bg-slate-950">
      <div className="relative">
        {imageUrl && !imageLoadFailed ? (
          <VerdictAssetImage
            imageUrl={imageUrl}
            assetSource={assetSource}
            screen={surface}
            trialId={assetId}
            alt={title}
            className="block w-full aspect-square object-cover"
            onAllFailed={() => setImageLoadFailed(true)}
          />
        ) : (
          <NoImageState
            heightClass="aspect-square"
            title={title}
            verdictLine=""
            winnerLabel={winnerLabel}
            loserLabel={loserLabel}
            sideA={sideA}
            sideB={sideB}
            isDaily={isDaily}
          />
        )}
      </div>
    </div>
  );
};

export default function VerdictMemeFrame({
  id,
  title,
  sideA,
  sideB,
  verdictLine,
  verdictLineShort,
  verdictLineFull,
  imageUrl,
  assetSource,
  emoji,
  winnerLabel,
  loserLabel,
  isDaily = false,
  isClose = false,
  isLandslide = false,
  renderState = 'ready',
  heightClass = 'h-40',
  showEmoji = true,
  surface = 'unknown',
  suppressLocalOverlayText = false,
}) {
  const fullVerdictLine = verdictLineFull || verdictLine || verdictLineShort || '';
  const overlayLine = deriveOverlayVerdictLine(fullVerdictLine);
  const explicitImageUrl = typeof imageUrl === 'string' && imageUrl.trim().length > 0 ? imageUrl.trim() : null;
  const synthesizedAssetSource = {
    ...(assetSource || {}),
    ...(id ? { id, trial_id: id } : {}),
    ...(explicitImageUrl ? { image_url: explicitImageUrl } : {}),
  };
  const canonicalImageUrl = explicitImageUrl || getCanonicalRenderedVerdictAsset(synthesizedAssetSource).url;
  const isVerdictCardSvg = isVerdictCardSvgAsset(canonicalImageUrl);
  const isPremiumVerdictCardSvg = isPremiumVerdictCardSvgAsset(canonicalImageUrl);
  const renderOverlayText = !isVerdictCardSvg && !suppressLocalOverlayText;
  const useComposedPremiumAssetOnly =
    suppressLocalOverlayText
    && isPremiumVerdictCardSvg
    && PREMIUM_COMPOSED_SURFACES.has(surface);
  const format = selectVerdictFormat({
    id,
    verdictLine: overlayLine,
    hasImage: Boolean(canonicalImageUrl),
  });
  const styleVariant = selectStyleVariant({
    id,
    format,
  });
  const textClass = styleClasses[styleVariant] || styleClasses.clean;

  const loggedRef = useRef(false);
  useEffect(() => {
    if (loggedRef.current) return;
    loggedRef.current = true;
    if (process.env.NODE_ENV !== 'production') {
      // Diagnostics for presentation decisions
      // eslint-disable-next-line no-console
      console.log('[verdict_render]', {
        id,
        surface,
        format,
        styleVariant,
        verdictLineLength: (verdictLine || '').length,
        imagePresent: Boolean(canonicalImageUrl),
        textPresent: Boolean(verdictLine),
        renderState,
        isDaily,
        composedPremiumAssetOnly: useComposedPremiumAssetOnly,
        isPremiumVerdictCardSvg,
      });
      console.log('[verdict_frontend_overlay_mode]', {
        asset_url: canonicalImageUrl || null,
        is_verdict_card_svg: isVerdictCardSvg,
        overlay_text_rendered: renderOverlayText,
        screen: surface === 'detail-trial' ? 'trial_detail' : surface,
      });
    }
  }, [id, surface, format, styleVariant, verdictLine, canonicalImageUrl, renderState, isDaily, isVerdictCardSvg, isPremiumVerdictCardSvg, renderOverlayText, useComposedPremiumAssetOnly]);

  const commonProps = {
    imageUrl: canonicalImageUrl,
    assetSource: synthesizedAssetSource,
    assetId: id,
    title,
    sideA,
    sideB,
    verdictLine: overlayLine,
    winnerLabel,
    loserLabel,
    emoji,
    heightClass,
    showEmoji,
    isDaily,
    textClass,
    surface,
    renderOverlayText,
  };

  if (renderState === 'processing') {
    return (
      <div className={`relative w-full ${heightClass} bg-slate-900/70 border border-slate-800 rounded-lg flex items-center justify-center`}>
        <div className="text-sm text-slate-200 font-semibold">Generating verdict…</div>
      </div>
    );
  }
  if (renderState === 'failed') {
    return (
      <div className={`relative w-full ${heightClass} bg-slate-900/70 border border-slate-800 rounded-lg flex items-center justify-center`}>
        <div className="text-sm text-rose-300 font-semibold">Verdict failed to render</div>
      </div>
    );
  }

  if (useComposedPremiumAssetOnly) {
    return (
      <div data-verdict-format="premium-composed">
        <PremiumComposedFrame
          imageUrl={canonicalImageUrl}
          assetSource={synthesizedAssetSource}
          assetId={id}
          title={title}
          winnerLabel={winnerLabel}
          loserLabel={loserLabel}
          sideA={sideA}
          sideB={sideB}
          isDaily={isDaily}
          surface={surface}
        />
      </div>
    );
  }

  let frame = <CaptionFrame {...commonProps} />;
  if (format === 'overlay') frame = <OverlayFrame {...commonProps} />;
  if (format === 'sticker') frame = <StickerFrame {...commonProps} />;

  return (
    <div data-verdict-format={format}>
      {frame}
    </div>
  );
}

export { selectVerdictFormat };
